<script>
    import $locale from '../../global/locale';
    import iconWarning from '../../icon/color/warning.svg';

    function defaultLayout() {
        if ($build.platform == 'web' || $build.platform == 'marketing') {
            try {
                // use body scroll unless we are in an iframe
                return window.self === window.top ? 'scroll' : 'full';
            } catch (e) {
                return 'full';
            }
        } else {
            return 'full';
        }
    }


    /**
     * `ib-application` is the top level view in your application. There is generally one in each front end project.
     * It sets the overall layout mode and provides a scope for translations and overlays. It can be nested inside
     * another `ib-application` from a different project; the subapplications in Client Portal are an example of this.
     */
    export default {
        name: 'ib-application',

        props: {
            /**
             * Layout mode:
             * - `scroll`: Fill at least the height of the window and display a vertical scrollbar; scrolling will be
             *   handled by the document body. This is the default for web/marketing. When used for web/marketing,
             *   application width is limited to 1400px.
             * - `full`: Fill the entire window and handle any scrolling inside the application container. This is the
             *   default for aTWS/iTWS/dTWS. When used for web/marketing, application width is limited to 1400px.
             * - `widget`: Fit the application to the height of its content and do not apply a background; this is
             *   useful when displaying a dynamically sized WebView inlined in aTWS/iTWS/dTWS.
             *
             * This setting is not meaningful for applications embedded inside other applications.
             */
            layout: String,
            /**
             * If true, do not limit width to 1400px for `web` and `marketing` platforms.
             */
            wide: Boolean,
            /**
             * Translations loaded either via `fetch()` at runtime or via `require()` at build time. Keys are IETF
             * locale strings; more than one of them may match the current locale.
             */
            translations: Object,
            /**
             * A function that is called to make preparations before displaying the application. This is typically used
             * to make initial API requests, for example, to test if the user has access and get user info. Return a
             * Promise from this function (for example, the return value from a `fetch` request); if an exception is
             * thrown or the Promise rejects, the exception or reason will be displayed in place of the `default` slot.
             */
            prepare: Function,
            /**
             * If true, treat this element as an `ib-row`.
             */
            row: Boolean,
            /**
             * If true and this is a row element, expand it to fill available vertical space.
             */
            grow: Boolean
        },

        data() {
            return {
                ready: false,
                error: false,
                top: false,
                direction: 'ltr',
                language: null,
                txStore: [],
                child: [],
                offset: {},
                dock: {}
            };
        },

        computed: {
            _class() {
                if (this.top) {
                    const layout = this.layout || defaultLayout();
                    return {
                        '_acap': !this.wide,
                        ['_a' + layout]: true
                    };
                }
            }
        },

        watch: {
            translations: 'loadTranslations',
            '$locale.current': 'loadTranslations',
            layout: 'setLayout'
        },

        beforeCreate() {
            // If not root application, clone $root to create new translation scope
            if (this.$root.__app) {
                const root = { ...this.$root };
                Object.keys(this.$slots).forEach(slot => {
                    this.$slots[slot].forEach(ref => {
                        if (ref.context) {
                            ref.context.$root = root;
                        }
                    });
                });
                this.$root = root;
            }
            this.$root.__app = this;
            this.$root.__txl = {};
            this.root = !window.__app;
        },

        created() {
            this.txStore = { ...this.translations };
            this.loadTranslations();

            try {
                const onPrepare = this.prepare ? this.prepare() : null;
                if (onPrepare instanceof Promise) {
                    onPrepare
                        .then(() => {
                            this.ready = true;
                        })
                        .catch(error => {
                            this.error = error || true;
                            this.ready = true;
                        });
                } else {
                    this.ready = true;
                }
            } catch (error) {
                this.error = error || true;
                this.ready = true;
            }

            if (this.root) {
                window.__app = this;
                addEventListener('keydown', this.keydown, true);
            }
        },

        mounted() {
            this.top = this.$el.parentElement === document.body;
            this.setLayout(this.layout);
        },

        beforeDestroy() {
            this.setLayout(void 0, this.layout);
            if (this.root) {
                if (this.hadOffset) {
                    document.body.style.setProperty('padding', '');
                }
                removeEventListener('keydown', this.keydown, true);
                delete window.__app;
                delete this.$root.__app;
                delete this.$root.__txl;
            }
        },

        render(h) {
            const vm = this;
            const error = vm.error;
            const language = vm.language;
            // vm._t() renders a slot
            // vm._e() renders a comment; without it, keep-alive does not work
            // vm._l() renders an array; without it, transition groups don't work

            // Satisfy vue-docgen-api
            if (0) {
                /**
                 * @slot This slot accepts a header bar, or other content that appears at the top of the page. It is
                 * not width constrained, and is always rendered immediately.
                 */
                const sheader = this.$slots.header;
                /**
                 * @slot This slot accepts a footer bar, or other content that appears at the bottom of the page. It is
                 * not width constrained, and is always rendered immediately.
                 */
                const sfooter = this.$slots.footer;
                /**
                 * @slot Your main application content goes in the `default` slot. It is rendered when the Promise
                 * returned from `prepare` resolves, or immediately if there is no `prepare` function. This slot's
                 * width is limited to 1400px for the `web` and `marketing` platforms.
                 */
                const sdefault = this.$slots.default;
                /**
                 * @slot This slot can be used for custom display of errors that occur during `prepare`. The exception
                 * or rejection reason will be bound as `error`.
                 */
                const serror = this.$slots.error;
            }

            const sheader = language && vm._t('header');
            const sfooter = language && vm._t('footer');
            const sdefault = language && vm.ready && vm._t('default');

            const rerror = error && vm._t('error', [
                h('div', {
                    'staticClass': 'icon-64',
                    'domProps': {
                        innerHTML: iconWarning
                    }
                }),
                h('h3', {
                    'staticClass': 'before-12 fg50 text-center'
                }, [
                    vm._v(vm._s(vm.error))
                ])
            ]);

            if (this.root) {
                const dock = vm.dock;
                const layout = vm.layout;

                // Apply padding to offset fixed position bars
                let hasOffset;
                const offset = Object.keys(vm.offset).reduce((offset,uid) => {
                    const o = vm.offset[uid];
                    offset[o.position] = o.size;
                    hasOffset = true;
                    return offset;
                }, {});
                const padding = hasOffset ?
                    (offset.top ? offset.top + 'px' : 0) + ' ' +
                    (offset.right ? offset.right + 'px' : 0) + ' ' +
                    (offset.bottom ? offset.bottom + 'px' : 0) + ' ' +
                    (offset.left ? offset.left + 'px' : 0) : '';

                // Apply offset from bars to drawers docked on the same edge
                vm.child.forEach(child => {
                    const hosted = child.attrs.hosted;
                    if (hosted && hosted.dodge_ !== void 0) {
                        hosted.dodge_ = offset[hosted.actual] || 0;
                    }
                });

                // Apply offset padding to body for widget layout
                if (vm.hadOffset || hasOffset) {
                    const root = document.documentElement;
                    root.style.setProperty('--padding-top', (offset.top || 0) + 'px');
                    root.style.setProperty('--padding-right', (offset.right || 0) + 'px');
                    root.style.setProperty('--padding-bottom', (offset.bottom || 0) + 'px');
                    root.style.setProperty('--padding-left', (offset.left || 0) + 'px');
                    if ((vm.hadOffset = hasOffset && layout == 'widget')) {
                        document.body.style.setProperty('padding', padding);
                    }
                }

                return h('div', {
                    'staticClass': '_app _con',
                    'class': vm._class,
                    'style': hasOffset && layout != 'widget' ? { padding } : void 0,
                    'attrs': {
                        dir: vm.direction
                    }
                }, language ? [
                    sheader
                        ? h('header', sheader)
                        : vm._e(),

                    rerror || sdefault
                        ? h('main', {
                                'staticClass': 'ib-cap',
                                'class': {
                                    'ib-row': !error && vm.row,
                                    'grow': !error && vm.grow,
                                    '_app-error': error
                                }
                            }, rerror || sdefault)
                        : vm._e(),

                    sfooter
                        ? h('footer', sfooter)
                        : vm._e(),

                    vm._l(Object.keys(dock), (position,i) => h('transition-group', {
                            'key': position,
                            'tag': 'div',
                            'staticClass': '_app-dock',
                            'class': '_p' + position,
                            'attrs': {
                                'name': '_tr-dock',
                                'appear': true
                            },
                            'on': {
                                'after-leave': () => {
                                    if (!dock[position].length) {
                                        Vue.delete(dock, position);
                                    }
                                }
                            }
                        }, vm._l(dock[position], e => h(e.is, {
                            'key': e.key,
                            'attrs': { ...e.attrs },
                            'on': {
                                'dismiss': () => {
                                    dock[position].splice(i, 1);
                                }
                            }
                        })))),

                    vm._l(vm.child, child => h(child.is, {
                        'key': child.key,
                        'attrs': {...child.attrs},
                        'on': child.on
                    }))
                ] : vm._e());

            } else {

                // Child application
                return h('section', {
                    'staticClass': '_con',
                    'class': {
                        'ib-row': !error && vm.row,
                        'grow': !error && vm.grow,
                        '_app-error': error
                    },
                    'attrs': {
                        dir: vm.direction != this.$root.__app.direction ? vm.direction : void 0
                    }
                }, [
                    ...(sheader || []),
                    ...(rerror || sdefault || []),
                    ...(sfooter || [])
                ]);
            }
        },

        methods: {
            setLayout(to, from) {
                if (this.top) {
                    const lfrom = from || defaultLayout();
                    document.documentElement.classList.remove('_l' + lfrom);
                    if (this.scrollTo) {
                        window.scrollTo = this.scrollTo;
                        window.scrollBy = this.scrollBy;
                        this.scrollTo = void 0;
                        this.scrollBy = void 0;
                    }
                    const lto = to || defaultLayout();
                    document.documentElement.classList.add('_l' + lto);
                    if (lto == 'full') {
                        // Overload `window.scrollTo` and `window.scrollBy`
                        this.scrollTo = window.scrollTo;
                        this.scrollBy = window.scrollBy;
                        window.scrollTo = (...args) => this.$el.scrollTo(...args);
                        window.scrollBy = (...args) => this.$el.scrollBy(...args);
                    }
                }
            },

            registerOffset(uid, position, size) {
                if (size) {
                    Vue.set(this.offset, uid, { position, size });
                } else {
                    Vue.delete(this.offset, uid);
                }
            },

            loadTranslations() {
                const txStore = this.txStore;
                const language = $locale.language;
                const region = $locale.region;
                const script = $locale.script;

                let all = [
                    language,
                    language + '-' + script,
                    language + '-' + region,
                    language + '-' + script + '-' + region
                ].filter(lc => txStore.hasOwnProperty(lc));

                // If no translations available for current locale, trigger 'en'
                if (!all.length && txStore['en']) {
                    all = ['en'];
                }

                // Load translations not previously loaded
                Promise.all(all
                    .filter(language => typeof txStore[language] === 'string')
                    .map(language => fetch(txStore[language])
                        .then(response => response.json())
                        .then(response => txStore[language] = response)
                        .catch(error => {
                            console.error('Failed to load ' + txStore[language]);
                        })))
                    .then(this.mapTranslations);
            },

            mapTranslations() {
                const language = $locale.language;
                const txStore = this.txStore;

                this.direction = (language == 'ar' || language == 'he') && Object.keys(this.translations||{}).some(key => key.startsWith(language) && this.translations[key] != null)
                    ? 'rtl'
                    : 'ltr';

                this.$root.__txl = {
                    ...txStore['en'],
                    ...txStore[language],
                    ...txStore[language + '-' + $locale.script],
                    ...txStore[language + '-' + $locale.region],
                    ...txStore[language + '-' + $locale.script + '-' + $locale.region]
                };

                this.$t = function(key, replacements, def) {
                    const result = this.$root.__txl[key] || (def === void 0 ? key : def);
                    return replacements === void 0
                        ? result
                        : result.replace(/{(\w+)}/g, (all, key) => {
                            const val = replacements[key];
                            return val === void 0 ? all : val;
                        });
                };

                this.language = language || '?';
            },

            keydown(e) {
                if (e.which == 9) {
                    this.tabts = e.timeStamp + 300;
                }
            }
        }
    }
</script>

<style lang="less">
    @import "../../less/globals.less";

    ._app {
        display: flex;
        flex-direction: column;
        flex: 1 1 auto;
        position: relative;

        &-error {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            flex: 1 1 auto;
        }
        > main {
            flex: 1 0 auto;
        }
        > header,
        > footer {
            flex: 0 0 auto;
        }

        &-dock {
            display: flex;
            position: fixed;
            right: 8px;
            left: 8px;
            flex-direction: column;
            visibility: hidden;
            z-index: @z-notify;

            &._ptop,
            &._ptop-start,
            &._ptop-end {
                top: 8px;
                max-height: calc(~'100% - 16px');
                justify-content: flex-start;
            }
            &._pbottom,
            &._pbottom-start,
            &._pbottom-end {
                bottom: 8px;
                max-height: calc(~'100% - 16px');
                justify-content: flex-end;
            }
            &._ptop,
            &._pbottom {
                align-items: center;
            }
            &._ptop-start,
            &._pbottom-start {
                align-items: flex-start;
            }
            &._ptop-end,
            &._pbottom-end {
                align-items: flex-end;
            }
        }
    }

    /** Layout */
    @media screen {
        ._lscroll,
        ._lwide,
        ._lfull {
            width: 100%;
            height: 100%;
            > body {
                width: 100%;
                height: 100%;
                background: @bg;
            }
        }
        ._lscroll,
        ._lwide {
            > body {
                overflow-x: hidden;
                overflow-y: scroll;
            }
        }
        ._afull {
            width: 100%;
            height: 100%;
            overflow: auto;
            -webkit-overflow-scrolling: touch;
        }
        ._ascroll,
        ._awide {
            height: auto;
            min-height: 100%;
        }
    }

    /** Animation */
    ._tr-dock {
        &-enter {
            transform: scale(.7);
        }
        &-enter,
        &-leave-to {
            opacity: 0;
        }
    }
</style>

<ib-example id="translations" name="Translations">
    <template lang="pug">
        ib-application(:translations="translations")
            p {{$t('Snapshot_As_Of', { when: $date.short($date.local()) })}}
    </template>
    <script>
        {
            data() {
                return {
                    translations: {
                        en: { Snapshot_As_Of: 'As Of: {when}' },
                        de: { Snapshot_As_Of: 'Zum Stand von: {when}' },
                        es: { Snapshot_As_Of: 'Al: {when}' },
                        fr: { Snapshot_As_Of: 'Au : {when}' },
                        it: { Snapshot_As_Of: 'Aggiornata al/alle: {when}' },
                        ja: { Snapshot_As_Of: '次の時点における: {when}' },
                        ru: { Snapshot_As_Of: 'По состоянию на: {when}' },
                        zh: { Snapshot_As_Of: '截至：{when}' },
                        ar: { Snapshot_As_Of: 'اعتبارا من: {when}' },
                        he: { Snapshot_As_Of: 'נכון ל: {when}' }
                    }
                };
            }
        }
    </script>
</ib-example>

<ib-example id="prepare" name="Prepare/Throw">
    <template lang="pug">
        ib-application(:prepare="prepare") Hello, world.
    </template>
    <script>
        {
            methods: {
                prepare() {
                    throw 'Goodbye, world.';
                }
            }
        }
    </script>
</ib-example>

<ib-example id="row" name="Two Columns">
    <template lang="pug">
        ib-application(row)
            ib-col(width="192px", class="inset-8 bg-gray10")
                h5(class="before-8 after-8") Table of Contents
                p(v-for="i in 8") Entry
            ib-col(width="auto", class="inset-8")
                h2(class="after-8") Main Document
                p(v-for="i in 8") Content
    </template>
</ib-example>
